import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Send, CheckCircle2, Mail, Phone, Calculator } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n";
import { CONTACT } from "../lib/contact";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { trackWhatsApp, trackPhone, trackEmail, trackLeadSubmit } from "../lib/analytics";
import { DIAL_CODES } from "../lib/dialCodes";

const API = "/api";

// Label helpers (extracted to avoid nested ternaries in JSX)
const simulatorToggleLabel = (isOpen, lang) => {
  if (isOpen) return lang === "pt" ? "Fechar simulador" : "Close estimator";
  return lang === "pt" ? "Calcular prestação primeiro" : "Estimate payment first";
};
const submitButtonLabel = (isLoading, lang, defaultLabel) => {
  if (isLoading) return lang === "pt" ? "A enviar..." : "Sending...";
  return defaultLabel;
};

const initial = {
  name: "",
  email: "",
  phone: "",
  nationality: "",
  residency_status: "",
  service_interest: "",
  budget: "",
  property_found: "",
  urgency: "",
  message: "",
};

// ─── Mini simulador ───────────────────────────────────────────────────────────
const EURIBOR_6M = 2.75; // atualizar manualmente quando necessário

function calcPrestacao(montante, anos, spread) {
  const taxa = (EURIBOR_6M + spread) / 100 / 12;
  const n = anos * 12;
  if (taxa === 0) return montante / n;
  return (montante * taxa * Math.pow(1 + taxa, n)) / (Math.pow(1 + taxa, n) - 1);
}

const Simulador = ({ onUseValues, lang }) => {
  const [montante, setMontante] = useState(250000);
  const [anos, setAnos] = useState(30);
  const [spread, setSpread] = useState(1.0);
  const prestacao = calcPrestacao(montante, anos, spread);
  const taeg = EURIBOR_6M + spread;

  return (
    <div className="mb-8 p-6 rounded-2xl border border-white/10 bg-white/[0.03]">
      <div className="flex items-center gap-2 mb-5">
        <Calculator className="w-4 h-4 text-[#A60056]" />
        <span className="text-sm font-medium text-white/80 uppercase tracking-wider">
          {lang === "pt" ? "Simulador de Prestação" : "Payment Estimator"}
        </span>
        <span className="text-xs text-white/40 ml-auto">
          Euribor 6M: {EURIBOR_6M.toFixed(2)}%
        </span>
      </div>

      <div className="space-y-5">
        <div>
          <div className="flex justify-between mb-2">
            <span className="text-xs text-white/55 uppercase tracking-wider">
              {lang === "pt" ? "Montante" : "Amount"}
            </span>
            <span className="text-sm font-medium text-white">
              {montante.toLocaleString("pt-PT")} €
            </span>
          </div>
          <input
            type="range"
            min={50000}
            max={1000000}
            step={10000}
            value={montante}
            onChange={(e) => setMontante(Number(e.target.value))}
            className="w-full accent-[#A60056]"
          />
          <div className="flex justify-between text-[11px] text-white/30 mt-1">
            <span>50 000 €</span><span>1 000 000 €</span>
          </div>
        </div>

        <div>
          <div className="flex justify-between mb-2">
            <span className="text-xs text-white/55 uppercase tracking-wider">
              {lang === "pt" ? "Prazo" : "Term"}
            </span>
            <span className="text-sm font-medium text-white">{anos} {lang === "pt" ? "anos" : "years"}</span>
          </div>
          <input
            type="range"
            min={10}
            max={40}
            step={1}
            value={anos}
            onChange={(e) => setAnos(Number(e.target.value))}
            className="w-full accent-[#A60056]"
          />
          <div className="flex justify-between text-[11px] text-white/30 mt-1">
            <span>10</span><span>40</span>
          </div>
        </div>

        <div>
          <div className="flex justify-between mb-2">
            <span className="text-xs text-white/55 uppercase tracking-wider">Spread</span>
            <span className="text-sm font-medium text-white">{spread.toFixed(2)}%</span>
          </div>
          <input
            type="range"
            min={0.5}
            max={2.5}
            step={0.05}
            value={spread}
            onChange={(e) => setSpread(Number(e.target.value))}
            className="w-full accent-[#A60056]"
          />
          <div className="flex justify-between text-[11px] text-white/30 mt-1">
            <span>0.50%</span><span>2.50%</span>
          </div>
        </div>
      </div>

      <div className="mt-5 p-4 rounded-xl bg-[#A60056]/10 border border-[#A60056]/20 flex items-center justify-between">
        <div>
          <div className="text-xs text-white/50 uppercase tracking-wider mb-1">
            {lang === "pt" ? "Prestação estimada" : "Estimated monthly payment"}
          </div>
          <div className="text-2xl font-medium text-white">
            {prestacao.toLocaleString("pt-PT", { maximumFractionDigits: 0 })} €
            <span className="text-sm text-white/40 font-normal">/mês</span>
          </div>
          <div className="text-[11px] text-white/40 mt-1">TAN indicativa: {taeg.toFixed(2)}%</div>
        </div>
        <button
          type="button"
          onClick={() => onUseValues(montante, anos)}
          className="btn-primary text-sm px-4 py-2"
        >
          {lang === "pt" ? "Usar estes valores" : "Use these values"}
        </button>
      </div>
      <p className="text-[10px] text-white/25 mt-3 leading-relaxed">
        {lang === "pt"
          ? "Simulação meramente indicativa. Sujeita a análise bancária e condições de mercado."
          : "Indicative estimate only. Subject to bank analysis and market conditions."}
      </p>
    </div>
  );
};

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="text-xs text-white/55 uppercase tracking-wider mb-2 block">{label}</span>
    {children}
  </label>
);

// ─── LeadForm principal ───────────────────────────────────────────────────────
const LeadForm = () => {
  const { t, lang } = useI18n();
  const [form, setForm] = useState(initial);
  const [dial, setDial] = useState("+351");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [showSim, setShowSim] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSimValues = (montante, anos) => {
    setForm((f) => ({
      ...f,
      budget: `${montante.toLocaleString("pt-PT")} €`,
      message: f.message || (lang === "pt"
        ? `Prazo pretendido: ${anos} anos.`
        : `Desired term: ${anos} years.`),
    }));
    setShowSim(false);
    toast.success(lang === "pt" ? "Valores aplicados ao formulário." : "Values applied to form.");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error(lang === "pt" ? "Preencha nome, email e telefone." : "Please fill name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, phone: `${dial} ${form.phone.trim()}`, language: lang, source: "landing_page" });
      trackLeadSubmit({
        service_interest: form.service_interest || "unknown",
        residency_status: form.residency_status || "unknown",
        property_found: form.property_found || "unknown",
        urgency: form.urgency || "unknown",
        language: lang,
      });
      setDone(true);
      toast.success(t.form.success);
      setForm(initial);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("[LeadForm] submission failed:", err);
      }
      toast.error(t.form.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0A0A0C] border border-white/10 rounded-xl px-4 py-3 text-white text-[15px] placeholder:text-white/30 focus:border-[#A60056] focus:outline-none focus:ring-1 focus:ring-[#A60056] transition-all";

  const urgencyOptions = lang === "pt"
    ? [
        { value: "urgent", label: "🔴 Urgente — preciso avançar já" },
        { value: "1-3months", label: "🟡 1 a 3 meses" },
        { value: "exploring", label: "🟢 Ainda a explorar opções" },
      ]
    : [
        { value: "urgent", label: "🔴 Urgent — ready to move now" },
        { value: "1-3months", label: "🟡 Within 1–3 months" },
        { value: "exploring", label: "🟢 Still exploring options" },
      ];

  return (
    <section
      id="contact"
      data-testid="lead-form-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-5 gap-12 lg:gap-16">
        {/* ── Coluna esquerda ── */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-2"
        >
          <div className="text-xs tracking-[0.25em] uppercase text-[#A60056] font-semibold mb-4">
            {lang === "pt" ? "Vamos conversar" : "Let's talk"}
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white mb-5 text-balance">
            {t.form.title}
          </h2>
          <p className="text-white/60 leading-relaxed mb-10">{t.form.subtitle}</p>

          <div className="space-y-4">
            <a
              href={`tel:${CONTACT.phone}`}
              onClick={() => trackPhone("contact_card")}
              className="flex items-center gap-4 p-4 rounded-2xl glass hover:border-white/20 transition-colors group"
              data-testid="contact-phone"
            >
              <div className="w-11 h-11 rounded-xl bg-[#A60056]/15 border border-[#A60056]/25 flex items-center justify-center">
                <Phone className="w-4 h-4 text-[#A60056]" />
              </div>
              <div>
                <div className="text-xs text-white/40 uppercase tracking-wider">{lang === "pt" ? "Telefone" : "Phone"}</div>
                <div className="text-white font-medium">{CONTACT.phoneDisplay}</div>
              </div>
            </a>
            <a
              href={`mailto:${CONTACT.email}`}
              onClick={() => trackEmail("contact_card")}
              className="flex items-center gap-4 p-4 rounded-2xl glass hover:border-white/20 transition-colors group"
              data-testid="contact-email"
            >
              <div className="w-11 h-11 rounded-xl bg-[#A60056]/15 border border-[#A60056]/25 flex items-center justify-center">
                <Mail className="w-4 h-4 text-[#A60056]" />
              </div>
              <div>
                <div className="text-xs text-white/40 uppercase tracking-wider">Email</div>
                <div className="text-white font-medium break-all">{CONTACT.email}</div>
              </div>
            </a>
            <a
              href={CONTACT.whatsapp()}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackWhatsApp("contact_card")}
              className="flex items-center gap-4 p-4 rounded-2xl glass hover:border-white/20 transition-colors"
              data-testid="contact-whatsapp"
            >
              <div className="w-11 h-11 rounded-xl bg-[#25D366]/15 border border-[#25D366]/30 flex items-center justify-center">
                <WhatsAppIcon className="w-4 h-4 text-[#25D366]" />
              </div>
              <div>
                <div className="text-xs text-white/40 uppercase tracking-wider">WhatsApp</div>
                <div className="text-white font-medium">{CONTACT.phoneDisplay}</div>
              </div>
            </a>
          </div>
        </motion.div>

        {/* ── Coluna direita — formulário ── */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-3"
        >
          {/* Toggle simulador */}
          <div className="mb-6 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setShowSim((v) => !v)}
              className="flex items-center gap-2 text-sm text-[#A60056] hover:text-[#c4006a] transition-colors"
            >
              <Calculator className="w-4 h-4" />
              {simulatorToggleLabel(showSim, lang)}
            </button>
          </div>

          {showSim && <Simulador onUseValues={handleSimValues} lang={lang} />}

          <form
            onSubmit={submit}
            className="glass-strong rounded-3xl p-7 md:p-10"
            data-testid="lead-form"
          >
            {done ? (
              <div className="text-center py-16" data-testid="form-success">
                <CheckCircle2 className="w-14 h-14 text-[#A60056] mx-auto mb-6" />
                <h3 className="text-2xl text-white font-medium mb-3">
                  {lang === "pt" ? "Obrigado!" : "Thank you!"}
                </h3>
                <p className="text-white/60">{t.form.success}</p>
                <a
                  href={CONTACT.reviewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block mt-6 text-sm font-medium text-[#A60056] border border-[#A60056]/40 rounded-full px-5 py-2.5 hover:bg-[#A60056]/10 transition-colors"
                  data-testid="success-review-link"
                >
                  {lang === "pt" ? "★ Deixe a sua avaliação no Google" : "★ Leave a Google review"}
                </a>
                <br />
                <button
                  onClick={() => setDone(false)}
                  className="mt-8 text-sm text-white/50 hover:text-white underline"
                >
                  {lang === "pt" ? "Enviar outro pedido" : "Send another request"}
                </button>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label={t.form.fields.name} testid="field-name">
                    <input required type="text" className={inputCls} value={form.name} onChange={update("name")} data-testid="input-name" />
                  </Field>
                  <Field label={t.form.fields.email} testid="field-email">
                    <input required type="email" className={inputCls} value={form.email} onChange={update("email")} data-testid="input-email" />
                  </Field>
                  <Field label={t.form.fields.phone} testid="field-phone">
                    <div className="flex gap-2">
                      <select
                        className={`${inputCls} !w-[7.5rem] flex-shrink-0`}
                        value={dial}
                        onChange={(e) => setDial(e.target.value)}
                        aria-label="Indicativo do país"
                        data-testid="input-dial"
                      >
                        {DIAL_CODES.map(([flag, name, code], i) => (
                          <option key={`${code}-${i}`} value={code}>
                            {flag} {code}
                          </option>
                        ))}
                      </select>
                      <input required type="tel" className={`${inputCls} flex-1 min-w-0`} value={form.phone} onChange={update("phone")} placeholder="912 345 678" data-testid="input-phone" />
                    </div>
                  </Field>
                  <Field label={t.form.fields.nationality} testid="field-nat">
                    <input type="text" className={inputCls} value={form.nationality} onChange={update("nationality")} data-testid="input-nationality" />
                  </Field>
                  <Field label={t.form.fields.residency} testid="field-res">
                    <select className={inputCls} value={form.residency_status} onChange={update("residency_status")} data-testid="input-residency">
                      <option value="">—</option>
                      <option value="resident">{t.form.fields.residency_yes}</option>
                      <option value="non-resident">{t.form.fields.residency_no}</option>
                    </select>
                  </Field>
                  <Field label={t.form.fields.service} testid="field-svc">
                    <select className={inputCls} value={form.service_interest} onChange={update("service_interest")} data-testid="input-service">
                      <option value="">—</option>
                      <option value="purchase">{t.form.fields.service_purchase}</option>
                      <option value="refinancing">{t.form.fields.service_refi}</option>
                      <option value="non_resident">{t.form.fields.service_nr}</option>
                      <option value="equity">{t.form.fields.service_equity}</option>
                      <option value="insurance">{t.form.fields.service_insurance}</option>
                    </select>
                  </Field>
                </div>

                <div className="mt-5">
                  <Field label={t.form.fields.budget} testid="field-bud">
                    <input type="text" placeholder="€ 250 000" className={inputCls} value={form.budget} onChange={update("budget")} data-testid="input-budget" />
                  </Field>
                </div>

                {/* ── NOVOS CAMPOS DE QUALIFICAÇÃO ── */}
                <div className="grid sm:grid-cols-2 gap-5 mt-5">
                  <Field
                    label={lang === "pt" ? "Já tem imóvel identificado?" : "Have you found a property?"}
                    testid="field-property"
                  >
                    <select
                      className={inputCls}
                      value={form.property_found}
                      onChange={update("property_found")}
                      data-testid="input-property-found"
                    >
                      <option value="">—</option>
                      <option value="yes">{lang === "pt" ? "Sim, já tenho" : "Yes, already found"}</option>
                      <option value="negotiating">{lang === "pt" ? "Em negociação" : "In negotiation"}</option>
                      <option value="no">{lang === "pt" ? "Ainda à procura" : "Still searching"}</option>
                    </select>
                  </Field>

                  <Field
                    label={lang === "pt" ? "Quando quer avançar?" : "When do you want to proceed?"}
                    testid="field-urgency"
                  >
                    <select
                      className={inputCls}
                      value={form.urgency}
                      onChange={update("urgency")}
                      data-testid="input-urgency"
                    >
                      <option value="">—</option>
                      {urgencyOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="mt-5">
                  <Field label={t.form.fields.message} testid="field-msg">
                    <textarea rows={4} className={inputCls + " resize-none"} value={form.message} onChange={update("message")} data-testid="input-message" />
                  </Field>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full mt-7 disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="submit-lead"
                >
                  {submitButtonLabel(loading, lang, t.form.submit)}
                  {!loading && <Send className="w-4 h-4" />}
                </button>
                <p className="text-xs text-white/40 text-center mt-4">{t.form.privacy}</p>
              </>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default LeadForm;
